import { useState, useEffect } from 'react';
import { CloudOff, CloudRain } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { WeatherAlert } from '@/hooks/useWeatherAlerts';
import { WeatherAlertCard } from './WeatherAlertCard';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

interface WeatherAlertPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  openAlerts: WeatherAlert[];
  dismissAlert: (alertId: string) => Promise<void>;
  resolveAlert: (alertId: string) => Promise<void>;
  isLoading: boolean;
}

export function WeatherAlertPanel({ open, onOpenChange, openAlerts, dismissAlert, resolveAlert, isLoading }: WeatherAlertPanelProps) {
  const navigate = useNavigate();
  const { activeShop } = useAuth();
  const [technicians, setTechnicians] = useState<any[]>([]);

  useEffect(() => {
    const loadTechnicians = async () => {
      if (!activeShop?.id) return;
      
      const { data } = await supabase
        .from('technicians')
        .select('id, name')
        .eq('shop_id', activeShop.id)
        .eq('employment_status', 'Active');
      
      if (data) setTechnicians(data);
    };

    if (open) {
      loadTechnicians();
    }
  }, [activeShop?.id, open]);

  const handleDismiss = async (alertId: string) => {
    try {
      await dismissAlert(alertId);
      toast({
        title: 'Alert dismissed',
        description: 'The weather alert has been dismissed.'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to dismiss alert',
        variant: 'destructive'
      });
    }
  };

  const handleFindIndoor = (alert: any) => {
    sessionStorage.setItem('weather-reroute-context', JSON.stringify({
      ticketId: alert.ticket_id,
      reason: alert.reason,
      excludeOutdoorBays: true,
      alertId: alert.id
    }));
    
    onOpenChange(false);
    navigate(`/?reassign=${alert.ticket_id}&indoor=true`);
    
    toast({
      title: 'Find Indoor Bay',
      description: 'Select an indoor bay for this ticket.'
    });
  };

  const handleReschedule = (alert: any) => {
    onOpenChange(false);
    navigate(`/?reschedule=${alert.ticket_id}`);
    
    toast({
      title: 'Reschedule Ticket',
      description: 'Choose a new time for this ticket.'
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader className="pb-4 border-b">
          <SheetTitle className="flex items-center gap-2">
            <CloudRain className="h-5 w-5 text-warning" />
            Weather Alerts
            {openAlerts.length > 0 && (
              <span className="bg-warning text-warning-foreground text-xs px-2 py-0.5 rounded-full">
                {openAlerts.length}
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-120px)] pr-4 -mr-4">
          <div className="space-y-4 py-4">
            {isLoading ? (
              <div className="text-center text-muted-foreground py-8">
                Loading alerts...
              </div>
            ) : openAlerts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <CloudOff className="h-12 w-12 text-muted-foreground mb-3" />
                <p className="font-medium">No Weather Alerts</p>
                <p className="text-sm text-muted-foreground mt-1">
                  All outdoor tickets have clear weather forecasts
                </p>
              </div>
            ) : (
              openAlerts.map((alert) => (
                <WeatherAlertCard
                  key={alert.id}
                  alert={alert}
                  technicians={technicians}
                  onDismiss={() => handleDismiss(alert.id)}
                  onFindIndoor={() => handleFindIndoor(alert)}
                  onReschedule={() => handleReschedule(alert)}
                />
              ))
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
